import React from 'react'
import { StaticImage } from 'gatsby-plugin-image'
import { useTheme } from 'styled-components'
import { motion } from 'framer-motion'
import scrollTo from 'gatsby-plugin-smoothscroll'
import { useIntl } from 'react-intl'

import ogImageServerCh from '../assets/seo/1home-server-ch.webp'

import SEO from '../components/SEO'
import Header from '../components/Header'
import Footer from '../components/Footer'
import VideoWithLink from '../components/VideoWithLink'
import KnxToCrestronSteps from '../components/KnxToCrestronSteps'
import ControlsList from '../components/ControlsList'
import ProductPricing from '../components/ProductPricing'
import CrestronCoverGraphic from '../components/icons/crestron-cover-graphic'
import KnxServerIntegrators1 from '../components/icons/knx-server-integrators-1'
import KnxServerIntegrators2 from '../components/icons/knx-server-integrators-2'
import KnxServerIntegrators3 from '../components/icons/knx-server-integrators-3'
import KnxServerHomeOwners1 from '../components/icons/knx-server-home-owners-1'
import KnxServerHomeOwners2 from '../components/icons/knx-server-home-owners-2'
import KnxServerHomeOwners3 from '../components/icons/knx-server-home-owners-3'

import { PageType, OgType } from '../constants/data/seo'
import { ProductSections } from '../constants/data/products'

import { useScrollTrigger } from '../hooks/useScrollTrigger'

import { Container, Section } from '../styles/PageLayout'
import { TitleL, TitleM, BodyM, BodyS, BodyXS } from '../styles/Typography'
import { ButtonPrimary } from '../styles/components/ButtonStyled'
import { LinkAsButtonLocalizedOutline } from '../styles/components/LinkStyled'
import ServerCrestronStyled from '../styles/pages/ServerCrestronStyled'

const ServerCrestron = () => {
  const intl = useIntl()
  const theme = useTheme()

  const hasReachedCoverImage = useScrollTrigger('crestron-cover-image', 100)
  const hasReachedDevices = useScrollTrigger('devices-section', 100)

  return (
    <>
      <SEO
        title={intl.formatMessage({ id: 'page_crestron_seo_title' })}
        description={intl.formatMessage({ id: 'page_crestron_seo_description' })}
        ogImage={ogImageServerCh}
        pathname="/server-ch"
        keywords={[
          '1Home Server CH',
          'KNX to Crestron Home',
          'Crestron KNX integration',
          'KNX Crestron',
          'Crestron smart home',
          'import KNX devices to Crestron',
        ]}
        product="server-ch"
        pageType={PageType.Product}
        ogType={OgType.Website}
      />
      <Header />
      <ServerCrestronStyled>
        <Section $isCoverSection $alignCenter className="section-one">
          <Container $column $mobileColumn $alignItems="center">
            <div className="badge">
              <BodyXS $weight={500} className="badge-text">
                {intl.formatMessage({ id: 'page_crestron_text_1' })}
              </BodyXS>
            </div>
            <TitleL className="title">{intl.formatMessage({ id: 'page_crestron_title_1' })}</TitleL>
            <BodyM className="text">{intl.formatMessage({ id: 'page_crestron_text_2' }, { br: <br /> })}</BodyM>
            <div className="img-container">
              <CrestronCoverGraphic />
              <div className="img">
                <motion.div
                  id="crestron-cover-image"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={hasReachedCoverImage ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
                  transition={{
                    duration: 0.5,
                    delay: 1.0,
                    ease: [0.4, 0, 0.2, 1],
                  }}
                  className="motion-container"
                >
                  <StaticImage
                    src="https://www.1home.io/xassets/crestron-cover-server.webp"
                    alt={intl.formatMessage({ id: 'page_crestron_alt_1' })}
                    placeholder="none"
                    quality="100"
                    loading="eager"
                  />
                </motion.div>
              </div>
            </div>
            <div className="btn-group">
              <ButtonPrimary onClick={() => scrollTo('#pricing')}> {intl.formatMessage({ id: 'btn_buy' })}</ButtonPrimary>
              <LinkAsButtonLocalizedOutline $small to="/partner-shop-signup">
                {intl.formatMessage({ id: 'btn_become_a_partner' })}
              </LinkAsButtonLocalizedOutline>
            </div>
          </Container>
        </Section>

        <KnxToCrestronSteps
          link={
            <a href={intl.formatMessage({ id: 'server_ch_documentation' })} target="_blank" className="link">
              <BodyXS $color={theme.textHighlight} $weight={500}>
                {intl.formatMessage({ id: 'page_crestron_link_1' })}
              </BodyXS>
            </a>
          }
        />

        <Section $alignCenter className="section-two">
          <Container $column $mobileColumn $alignItems="center">
            <TitleM className="title">{intl.formatMessage({ id: 'page_crestron_title_2' })}</TitleM>
            <BodyM className="text">{intl.formatMessage({ id: 'page_crestron_text_6' })}</BodyM>
            <div className="subtitle-box">
              <BodyS $weight={500}>{intl.formatMessage({ id: 'page_crestron_text_7' })}</BodyS>
            </div>
            <motion.div
              id="devices-section"
              className="devices"
              initial={{ opacity: 0, y: 20 }}
              animate={hasReachedDevices ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="device">
                <StaticImage
                  className="device-img"
                  src="https://www.1home.io/xassets/crestron-4.webp"
                  alt={`1Home ${intl.formatMessage({ id: 'app' })}`}
                  placeholder="none"
                  quality="100"
                />
                <BodyM className="device-text">{intl.formatMessage({ id: 'page_crestron_text_7_1' })}</BodyM>
              </div>
              <div className="device">
                <StaticImage
                  className="device-img"
                  src="https://www.1home.io/xassets/crestron-5.webp"
                  alt={`1Home ${intl.formatMessage({ id: 'app' })}`}
                  placeholder="none"
                  quality="100"
                />
                <BodyM className="device-text">{intl.formatMessage({ id: 'page_crestron_text_7_2' })}</BodyM>
              </div>
              <div className="device">
                <StaticImage
                  className="device-img"
                  src="https://www.1home.io/xassets/crestron-6.webp"
                  alt={`1Home ${intl.formatMessage({ id: 'app' })}`}
                  placeholder="none"
                  quality="100"
                />
                <BodyM className="device-text">{intl.formatMessage({ id: 'page_crestron_text_7_3' })}</BodyM>
              </div>
              <div className="device">
                <StaticImage
                  className="device-img"
                  src="https://www.1home.io/xassets/crestron-7.webp"
                  alt={`1Home ${intl.formatMessage({ id: 'app' })}`}
                  placeholder="none"
                  quality="100"
                />
                <BodyM className="device-text">{intl.formatMessage({ id: 'page_crestron_text_7_4' })}</BodyM>
              </div>
            </motion.div>
            <div className="subtitle-box">
              <BodyS $weight={500}>{intl.formatMessage({ id: 'page_crestron_text_8' })}</BodyS>
            </div>
            <ControlsList />
          </Container>
        </Section>

        <Section $alignCenter className="section-three">
          <Container $column $mobileColumn $alignItems="center">
            <TitleM className="title">{intl.formatMessage({ id: 'page_crestron_title_3' })}</TitleM>
            <BodyM className="text">{intl.formatMessage({ id: 'page_crestron_text_9' })}</BodyM>
            <VideoWithLink
              link="https://youtu.be/On79lgyTrog"
              coverImg="https://www.1home.io/xassets/automations-block-video-cover.webp"
              alt={intl.formatMessage({ id: 'page_crestron_title_3' })}
              maxWidth="85%"
            />
            <div className="items">
              <div className="item">
                <div className="item-img">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M16 1.77778C8.14528 1.77778 1.77778 8.14528 1.77778 16C1.77778 23.8547 8.14528 30.2222 16 30.2222C23.8547 30.2222 30.2222 23.8547 30.2222 16C30.2222 8.14528 23.8547 1.77778 16 1.77778ZM0 16C0 7.16344 7.16344 0 16 0C24.8366 0 32 7.16344 32 16C32 24.8366 24.8366 32 16 32C7.16344 32 0 24.8366 0 16Z"
                      fill="#0D1116"
                    />
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M15.9983 5.62988C16.4892 5.62988 16.8872 6.02785 16.8872 6.51877V15.704H23.5538C24.0447 15.704 24.4427 16.1019 24.4427 16.5928C24.4427 17.0838 24.0447 17.4817 23.5538 17.4817H15.9983C15.5073 17.4817 15.1094 17.0838 15.1094 16.5928V6.51877C15.1094 6.02785 15.5073 5.62988 15.9983 5.62988Z"
                      fill="url(#paint0_linear_6749_274)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_6749_274"
                        x1="23.0946"
                        y1="8.26363"
                        x2="15.2269"
                        y2="10.8812"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#008996" />
                        <stop offset="1" stopColor="#52E8C1" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <BodyM $weight={700}>{intl.formatMessage({ id: 'block_automations_text_9_1' })}</BodyM>
                <BodyXS className="item-text">{intl.formatMessage({ id: 'block_automations_text_9_2' })}</BodyXS>
              </div>
              <div className="item">
                <div className="item-img">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M30.9146 1.0954C29.4671 -0.365133 27.1203 -0.365133 25.6728 1.0954L24.0389 2.74403L29.2807 8.03309L30.9146 6.38446C32.3621 4.92393 32.3621 2.55593 30.9146 1.0954Z"
                      fill="url(#paint0_linear_366_1843)"
                    />
                    <path
                      d="M27.783 9.54425L22.5412 4.25519L10.6792 16.2242C9.80827 17.1029 9.16808 18.1868 8.81646 19.3778L7.68717 23.2031C7.57615 23.5791 7.67831 23.9863 7.95327 24.2637C8.22823 24.5411 8.63176 24.6442 9.00443 24.5322L12.7955 23.3927C13.9759 23.0379 15.0501 22.392 15.921 21.5132L27.783 9.54425Z"
                      fill="url(#paint1_linear_366_1843)"
                    />
                    <path
                      d="M26.4643 19.0095V27.1459C26.4643 29.2744 24.7542 31 22.6446 31H4.81964C2.71011 31 1 29.2744 1 27.1459V9.16022C1 7.03168 2.71011 5.30615 4.81964 5.30615H12.8833"
                      stroke="#0F172A"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_366_1843"
                        x1="28.482"
                        y1="5.46149"
                        x2="9.07883"
                        y2="13.5858"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#008996" />
                        <stop offset="1" stopColor="#52E8C1" />
                      </linearGradient>
                      <linearGradient
                        id="paint1_linear_366_1843"
                        x1="28.482"
                        y1="5.46149"
                        x2="9.07883"
                        y2="13.5858"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#008996" />
                        <stop offset="1" stopColor="#52E8C1" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <BodyM $weight={700}>{intl.formatMessage({ id: 'block_automations_text_10_1' }, { br: <br /> })}</BodyM>
                <BodyXS className="item-text">{intl.formatMessage({ id: 'block_automations_text_10_2' })}</BodyXS>
              </div>
              <div className="item">
                <div className="item-img">
                  <svg width="28" height="32" viewBox="0 0 28 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M10.34 31.9684C10.2427 31.9684 10.1453 31.9369 10.0479 31.8895C9.80444 31.7632 9.69083 31.4791 9.75575 31.2107L13.1966 18.9296H0.601799C0.358343 18.9296 0.147348 18.7875 0.0499657 18.5665C-0.0474167 18.3455 0.00127438 18.093 0.163578 17.9193L17.2055 0.192161C17.4003 -0.0130507 17.6924 -0.0604073 17.9521 0.0816624C18.1956 0.207947 18.3092 0.492086 18.2443 0.76044L14.8034 13.0416H27.3982C27.6417 13.0416 27.8527 13.1837 27.95 13.4046C28.0474 13.6256 27.9987 13.8782 27.8364 14.0519L10.7945 31.8106C10.6809 31.9369 10.5186 32 10.3563 32L10.34 31.9684Z"
                      fill="url(#paint0_linear_366_1822)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_366_1822"
                        x1="23.9556"
                        y1="7.11111"
                        x2="0.891863"
                        y2="15.6372"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#008996" />
                        <stop offset="1" stopColor="#52E8C1" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <BodyM $weight={700}>{intl.formatMessage({ id: 'block_automations_text_4_1' })}</BodyM>
                <BodyXS className="item-text">{intl.formatMessage({ id: 'block_automations_text_4_2' })}</BodyXS>
              </div>
              <div className="item">
                <div className="item-img">
                  <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M30.373 32.0002H10.5901C9.6829 32.0002 8.9502 31.2677 8.9502 30.3609V22.4089C8.9502 22.0427 9.24677 21.7462 9.61312 21.7462H21.441C21.6155 21.7462 21.7725 21.5892 21.7725 21.4149V9.59138C21.7725 9.22516 22.0691 8.92871 22.4354 8.92871H30.3905C31.3151 8.92871 32.0303 9.6437 32.0303 10.568V30.3435C32.0303 31.2677 31.3151 31.9827 30.3905 31.9827L30.373 32.0002ZM10.2586 23.0541V30.3609C10.2586 30.5353 10.4156 30.6923 10.5901 30.6923H30.373C30.5649 30.6923 30.7045 30.5528 30.7045 30.3609V10.5854C30.7045 10.3936 30.5649 10.2541 30.373 10.2541H23.0634V21.4149C23.0634 22.3042 22.3133 23.0541 21.4236 23.0541H10.2586Z"
                      fill="#0D1116"
                    />
                    <path
                      d="M21.4215 23.0536H9.59359C9.22723 23.0536 8.93066 22.7571 8.93066 22.3909V10.5675C8.93066 9.66065 9.66337 8.92822 10.5705 8.92822H22.3984C22.7648 8.92822 23.0614 9.22468 23.0614 9.59089V21.4144C23.0614 22.3037 22.3112 23.0536 21.4215 23.0536Z"
                      fill="url(#paint0_linear_366_1833)"
                    />
                    <path
                      d="M6.28031 23.054H1.63986C0.732703 23.054 0 22.3216 0 21.4148V1.63924C0 0.714986 0.715257 0 1.63986 0H21.4228C22.33 0 23.0627 0.732425 23.0627 1.63924V6.40002C23.0627 6.76623 22.7661 7.06269 22.3998 7.06269C22.0334 7.06269 21.7368 6.76623 21.7368 6.40002V1.63924C21.7368 1.44741 21.5624 1.30791 21.4054 1.30791H1.63986C1.44796 1.30791 1.3084 1.44741 1.3084 1.63924V21.4148C1.3084 21.5892 1.46541 21.7461 1.63986 21.7461H6.28031C6.64666 21.7461 6.94323 22.0426 6.94323 22.4088C6.94323 22.775 6.64666 23.0715 6.28031 23.0715V23.054Z"
                      fill="#0D1116"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_366_1833"
                        x1="21.0203"
                        y1="12.0672"
                        x2="9.79522"
                        y2="16.8114"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#008996" />
                        <stop offset="1" stopColor="#52E8C1" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <BodyM $weight={700}>{intl.formatMessage({ id: 'block_automations_text_2_1' })}</BodyM>
                <BodyXS className="item-text">{intl.formatMessage({ id: 'block_automations_text_2_2' })}</BodyXS>
              </div>
              <div className="item">
                <div className="item-img">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5.11717 15.4313H1.45471C0.633227 15.4313 0 14.7983 0 13.9771V1.45417C0 0.632989 0.633227 0 1.45471 0H13.9995C14.8039 0 15.4542 0.632989 15.4542 1.45417V4.70466C15.4542 5.06393 15.1633 5.35476 14.8039 5.35476C14.4445 5.35476 14.1535 5.06393 14.1535 4.70466V1.45417C14.1535 1.35152 14.0851 1.28309 13.9824 1.28309H1.4376C1.33492 1.28309 1.26646 1.35152 1.26646 1.45417V13.9771C1.26646 14.0798 1.33492 14.1482 1.4376 14.1482H5.10006C5.45946 14.1482 5.75041 14.439 5.75041 14.7983C5.75041 15.1576 5.45946 15.4484 5.10006 15.4484L5.11717 15.4313Z"
                      fill="#0D1116"
                    />
                    <path
                      d="M12.7332 22.6337C9.43011 22.6337 6.74316 19.9478 6.74316 16.646V12.4546C6.74316 9.15273 9.43011 6.4668 12.7332 6.4668H16.9091C20.2121 6.4668 22.8991 9.15273 22.8991 12.4546C22.8991 12.8138 22.6081 13.1047 22.2487 13.1047C21.8893 13.1047 21.5984 12.8138 21.5984 12.4546C21.5984 9.85416 19.4933 7.74989 16.8919 7.74989H12.7161C10.1147 7.74989 8.00963 9.85416 8.00963 12.4546V16.646C8.00963 19.2464 10.1147 21.3507 12.7161 21.3507C13.0755 21.3507 13.3664 21.6415 13.3664 22.0007C13.3664 22.36 13.0755 22.6508 12.7161 22.6508L12.7332 22.6337Z"
                      fill="#0D1116"
                    />
                    <path
                      d="M23.0835 31.7527C18.1546 31.7527 14.167 27.7494 14.167 22.8395C14.167 17.9295 18.1717 13.9263 23.0835 13.9263C27.9953 13.9263 32.0001 17.9295 32.0001 22.8395C32.0001 27.7494 27.9953 31.7527 23.0835 31.7527Z"
                      fill="url(#paint0_linear_366_1811)"
                    />
                    <defs>
                      <linearGradient
                        id="paint0_linear_366_1811"
                        x1="29.4242"
                        y1="17.8877"
                        x2="15.2581"
                        y2="23.8749"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#008996" />
                        <stop offset="1" stopColor="#52E8C1" />
                      </linearGradient>
                    </defs>
                  </svg>
                </div>
                <BodyM $weight={700}>{intl.formatMessage({ id: 'block_automations_text_8_1' })}</BodyM>
                <BodyXS className="item-text">{intl.formatMessage({ id: 'block_automations_text_8_2' })}</BodyXS>
              </div>
              <div className="item">
                <div className="item-img">
                  <svg width="30" height="32" viewBox="0 0 30 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_366_1815)">
                      <path
                        d="M9.06228 28.2599C8.97693 28.2599 8.89159 28.232 8.80624 28.1901C8.59287 28.0785 8.49329 27.8273 8.55019 27.59L11.5658 16.7326H0.527433C0.314061 16.7326 0.12914 16.607 0.0437912 16.4117C-0.0415573 16.2163 0.00111697 15.993 0.143364 15.8395L15.0794 0.167443C15.25 -0.0139792 15.5203 -0.0558457 15.7337 0.0697539C15.9471 0.181398 16.0466 0.432597 15.9897 0.669841L12.9741 11.5272H24.0125C24.2259 11.5272 24.4108 11.6528 24.4961 11.8482C24.5815 12.0436 24.5388 12.2669 24.3966 12.4204L22.59 14.3183C22.3909 14.5277 22.0495 14.5416 21.8361 14.3462C21.6227 14.1509 21.6085 13.8159 21.8077 13.6066L22.7749 12.5879H12.2629C12.0922 12.5879 11.9357 12.5181 11.8361 12.3785C11.7365 12.2529 11.7081 12.0855 11.7508 11.918L14.3823 2.44219L1.76499 15.6999H12.2771C12.4478 15.6999 12.6042 15.7697 12.7038 15.9093C12.8034 16.0349 12.8318 16.2023 12.7892 16.3698L10.1576 25.8456L11.7223 24.1988C11.9215 23.9895 12.2629 23.9756 12.4762 24.1709C12.6896 24.3663 12.7038 24.7012 12.5047 24.9106L9.46058 28.1064C9.361 28.218 9.21876 28.2739 9.07651 28.2739L9.06228 28.2599Z"
                        fill="#0D1116"
                      />
                      <path
                        d="M22.2184 16.7188C17.9226 16.7188 14.4375 20.1379 14.4375 24.3524C14.4375 28.567 17.9226 31.9861 22.2184 31.9861C26.5143 31.9861 29.9994 28.567 29.9994 24.3524C29.9994 20.1379 26.5143 16.7188 22.2184 16.7188ZM21.3081 27.1017C21.3081 27.4087 21.052 27.6599 20.7391 27.6599H19.786C19.4731 27.6599 19.217 27.4087 19.217 27.1017V21.6311C19.217 21.3241 19.4731 21.0729 19.786 21.0729H20.7391C21.052 21.0729 21.3081 21.3241 21.3081 21.6311V27.1017ZM25.2199 27.1017C25.2199 27.4087 24.9638 27.6599 24.6509 27.6599H23.6978C23.3849 27.6599 23.1288 27.4087 23.1288 27.1017V21.6311C23.1288 21.3241 23.3849 21.0729 23.6978 21.0729H24.6509C24.9638 21.0729 25.2199 21.3241 25.2199 21.6311V27.1017Z"
                        fill="url(#paint0_linear_366_1815)"
                      />
                    </g>
                    <defs>
                      <linearGradient
                        id="paint0_linear_366_1815"
                        x1="27.7515"
                        y1="20.1115"
                        x2="15.4607"
                        y2="25.4044"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#008996" />
                        <stop offset="1" stopColor="#52E8C1" />
                      </linearGradient>
                      <clipPath id="clip0_366_1815">
                        <rect width="30" height="32" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <BodyM $weight={700}>{intl.formatMessage({ id: 'block_automations_text_5_1' })}</BodyM>
                <BodyXS className="item-text">{intl.formatMessage({ id: 'block_automations_text_5_2' })}</BodyXS>
              </div>
              <div className="item">
                <div className="item-img">
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g clipPath="url(#clip0_366_1968)">
                      <path
                        d="M20.6797 9.31232C21.9321 9.31232 23.1524 9.52105 24.3405 9.9064C24.6617 10.0188 24.8222 10.356 24.7259 10.6771C24.6135 10.9982 24.2763 11.1588 23.9552 11.0624C22.5101 10.5808 20.9687 10.4202 19.4594 10.5968C19.1062 10.645 18.8333 10.4041 18.7851 10.067C18.753 9.72978 18.9778 9.44077 19.3149 9.3926C19.7806 9.34443 20.2301 9.31232 20.6958 9.31232L20.6797 9.31232ZM14.8674 11.0143C15.0601 11.0143 15.2527 11.1106 15.3812 11.2872C15.5578 11.5602 15.4775 11.9455 15.2046 12.1222C14.5944 12.5075 14.0164 12.9731 13.5026 13.503C12.9406 14.065 12.4589 14.6751 12.0415 15.3494C11.8649 15.6385 11.4956 15.7187 11.2066 15.5421C10.9176 15.3655 10.8373 14.9962 11.0139 14.7072C11.4795 13.9526 12.0254 13.2621 12.6516 12.6359C13.2296 12.0579 13.8719 11.5281 14.5623 11.0946C14.6586 11.0303 14.771 10.9982 14.8834 10.9982L14.8674 11.0143ZM28.2743 12.4112C28.4027 12.4112 28.5312 12.4593 28.6436 12.5717L28.7239 12.652C29.9441 13.8723 30.8433 15.3173 31.4213 16.939C31.5337 17.2601 31.3731 17.5973 31.052 17.7097C30.7309 17.8221 30.3937 17.6615 30.2813 17.3404C29.7675 15.8954 28.9647 14.6109 27.8729 13.519C27.6321 13.2782 27.5999 12.8607 27.8408 12.6199C27.9692 12.4914 28.1298 12.4272 28.2904 12.4272L28.2743 12.4112ZM9.97024 18.9621C9.97024 18.9621 10.0184 18.9621 10.0345 18.9621C10.3716 18.9942 10.6125 19.2993 10.5643 19.6204C10.5322 19.9736 10.5001 20.3269 10.5001 20.6962C10.5001 21.8683 10.6928 23.0243 11.0942 24.1161C11.2066 24.4373 11.046 24.7744 10.7249 24.8868C10.4038 24.9992 10.0666 24.8387 9.95418 24.5175C9.52067 23.2812 9.29588 21.9967 9.29588 20.6962C9.29588 20.2948 9.31193 19.8934 9.3601 19.4919C9.39221 19.1869 9.64911 18.946 9.95418 18.946L9.97024 18.9621ZM31.3892 21.2742C31.3892 21.2742 31.4374 21.2742 31.4534 21.2742C31.7906 21.3063 32.0154 21.6114 31.9833 21.9325C31.7906 23.6184 31.2286 25.2722 30.3295 26.7012C30.1529 26.9902 29.7836 27.0705 29.4946 26.8939C29.2056 26.7172 29.1253 26.3479 29.3019 26.0589C30.1047 24.7744 30.6185 23.2973 30.7791 21.788C30.8112 21.4829 31.0681 21.2581 31.3731 21.2581L31.3892 21.2742ZM13.1012 27.7448C13.2618 27.7448 13.4063 27.8091 13.5187 27.9214C14.6105 29.0133 15.911 29.8161 17.3561 30.3138C17.6772 30.4262 17.8378 30.7634 17.7254 31.0845C17.613 31.4056 17.2758 31.5662 16.9547 31.4538C15.333 30.8918 13.888 29.9927 12.6677 28.7885C12.4268 28.5476 12.4268 28.1783 12.6677 27.9375C12.7801 27.8251 12.9406 27.7609 13.1012 27.7609L13.1012 27.7448ZM26.4439 29.2059C26.6366 29.2059 26.8453 29.3023 26.9577 29.4789C27.1343 29.7519 27.054 30.1372 26.765 30.3138C25.336 31.213 23.6983 31.791 22.0124 31.9997C21.6591 32.0479 21.3862 31.807 21.338 31.4699C21.3059 31.1327 21.5307 30.8437 21.8679 30.7955C23.3772 30.6189 24.8543 30.1051 26.1228 29.2862C26.2191 29.222 26.3315 29.1899 26.4439 29.1899L26.4439 29.2059Z"
                        fill="#0D1116"
                      />
                      <path
                        d="M11.3702 0C17.6481 -5.48838e-07 22.754 5.10587 22.754 11.3838C22.754 17.6618 17.6481 22.7677 11.3702 22.7677C5.0922 22.7677 -0.0136713 17.6618 -0.0136719 11.3838C-0.0136724 5.10587 5.0922 5.48838e-07 11.3702 0Z"
                        fill="url(#paint0_linear_366_1968)"
                      />
                    </g>
                    <defs>
                      <linearGradient
                        id="paint0_linear_366_1968"
                        x1="19.4653"
                        y1="5.05949"
                        x2="1.37726"
                        y2="12.7014"
                        gradientUnits="userSpaceOnUse"
                      >
                        <stop stopColor="#008996" />
                        <stop offset="1" stopColor="#52E8C1" />
                      </linearGradient>
                      <clipPath id="clip0_366_1968">
                        <rect width="32" height="32" fill="white" transform="translate(32 32) rotate(180)" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>
                <BodyM $weight={700}>{intl.formatMessage({ id: 'block_automations_text_3_1' })}</BodyM>
                <BodyXS className="item-text">{intl.formatMessage({ id: 'block_automations_text_3_2' })}</BodyXS>
              </div>
            </div>
          </Container>
        </Section>

        <Section $alignCenter className="section-four">
          <Container $column $mobileColumn $alignItems="center">
            <TitleL className="title">{intl.formatMessage({ id: 'page_crestron_title_4' })}</TitleL>
            <TitleM className="subtitle">
              <span>{intl.formatMessage({ id: 'page_knx_server_title_6_2' })}</span> ⚡
            </TitleM>
            <BodyM className="text">{intl.formatMessage({ id: 'page_crestron_text_10' })}</BodyM>
            <div className="img-box">
              <div className="img">
                <img
                  src={`https://www.1home.io/xassets/secure-remote-access-scheme-${intl.locale}.webp`}
                  alt={intl.formatMessage({ id: 'page_knx_server_alt_6' })}
                />
              </div>
            </div>
          </Container>
        </Section>

        <Section $alignCenter className="section-five">
          <Container $column $mobileColumn $alignItems="center" $gap="5rem" $mobileGap="3rem">
            <TitleM className="title">{intl.formatMessage({ id: 'page_knx_server_title_7' })}</TitleM>
            <div className="item-group mb-2">
              <div className="item">
                <div className="img">
                  <KnxServerIntegrators1 />
                </div>
                <BodyM $weight={700} className="subtitle">
                  {intl.formatMessage({ id: 'page_knx_server_text_6_1' })}
                </BodyM>
                <BodyM className="text">{intl.formatMessage({ id: 'page_knx_server_text_6_2' })}</BodyM>
              </div>
              <div className="item">
                <div className="img">
                  <KnxServerIntegrators2 />
                </div>
                <BodyM $weight={700} className="subtitle">
                  {intl.formatMessage({ id: 'page_knx_server_text_7_1' })}
                </BodyM>
                <BodyM className="text">{intl.formatMessage({ id: 'page_knx_server_text_7_2' })}</BodyM>
              </div>
              <div className="item">
                <div className="img">
                  <KnxServerIntegrators3 />
                </div>
                <BodyM $weight={700} className="subtitle">
                  {intl.formatMessage({ id: 'page_knx_server_text_8_1' })}
                </BodyM>
                <BodyM className="text">{intl.formatMessage({ id: 'page_knx_server_text_8_2' })}</BodyM>
              </div>
            </div>
            <TitleM className="title">{intl.formatMessage({ id: 'page_knx_server_title_8' })}</TitleM>
            <div className="item-group">
              <div className="item">
                <div className="img">
                  <KnxServerHomeOwners1 />
                </div>
                <BodyM $weight={700} className="subtitle">
                  {intl.formatMessage({ id: 'page_knx_server_text_9_1' })}
                </BodyM>
                <BodyM className="text">{intl.formatMessage({ id: 'page_knx_server_text_9_2' })}</BodyM>
              </div>
              <div className="item">
                <div className="img">
                  <KnxServerHomeOwners2 />
                </div>
                <BodyM $weight={700} className="subtitle">
                  {intl.formatMessage({ id: 'page_knx_server_text_10_1' })}
                </BodyM>
                <BodyM className="text">{intl.formatMessage({ id: 'page_knx_server_text_10_2' })}</BodyM>
              </div>
              <div className="item">
                <div className="img">
                  <KnxServerHomeOwners3 />
                </div>
                <BodyM $weight={700} className="subtitle">
                  {intl.formatMessage({ id: 'page_knx_server_text_11_1' })}
                </BodyM>
                <BodyM className="text">{intl.formatMessage({ id: 'page_knx_server_text_11_2' })}</BodyM>
              </div>
            </div>
          </Container>
        </Section>

        <ProductPricing
          className="section-six"
          optTitle={<TitleL className="headline no-margin">{intl.formatMessage({ id: 'page_crestron_title_5' }, { br: <br /> })}</TitleL>}
          hideButtonGroup
          hideProductConditions
          productsToShow={[ProductSections.ServerKNX, ProductSections.ServerChrestron]}
        />
      </ServerCrestronStyled>

      <Footer />
    </>
  )
}

export default ServerCrestron
